import type { ComponentType } from 'react'
import AccountingTransactionsShell from 'src/app/AccountingTransactionsShell/AccountingTransactionsShell'
import ExpenseReportAccounting from 'src/app/ExpenseReport/ExpenseReportAccounting'
import ExpenseReportManagerReview from 'src/app/ExpenseReport/ExpenseReportManagerReview'
import ExpensesNeedsApproval from 'src/app/ExpensesNeedsApproval/ExpensesNeedsApproval'
import ManagerApproverExpenseReports from 'src/app/ManagerApproverExpenseReports/ManagerApproverExpenseReports'
import Onboarding from 'src/app/Onboarding/Onboarding'
import OnboardingCard from 'src/app/Onboarding/OnboardingCard'
import OrgInviteDecision from 'src/app/OrgInviteDecision/OrgInviteDecision'
import {
  budgetsEntityGatePaths,
  budgetsFamilyGatePaths,
} from 'src/navigation/budgetsRouteTree'
import type {
  AuthenticatedFlatAppRouteMetadata,
  FlatAppRouteMetadata,
} from 'src/navigation/flatArchitectureRouteMetadata'
import {
  AccountingReviewReportsFlatRouteMetadata,
  ActivityFlatRouteMetadata,
  DownloadsFlatRouteMetadata,
  ExpenseReportFlatRouteMetadata,
  FAQFlatRouteMetadata,
  InsightsFlatRouteMetadata,
  ManagerReviewReimbursementDetailFlatRouteMetadata,
  ManagerReviewReimbursementsFlatRouteMetadata,
  ManagerReviewTransactionsFlatRouteMetadata,
  MarcomPricingFlatRouteMetadata,
  OnboardingCardFlatRouteMetadata,
  OnboardingFlatRouteMetadata,
  OrgInviteDecisionFlatRouteMetadata,
  PricingFlatRouteMetadata,
  PrivacyFlatRouteMetadata,
  StreamingAgentFlatRouteMetadata,
  SupportFlatRouteMetadata,
  TermsFlatRouteMetadata,
} from 'src/navigation/flatArchitectureRouteMetadata'
import Activity from 'src/page-components/Activity/Activity'
import BudgetEntityPage from 'src/page-components/Budgets/BudgetEntityPage/BudgetEntityPage'
import BudgetsFamilyPage from 'src/page-components/Budgets/BudgetsFamilyPage/BudgetsFamilyPage'
import Downloads from 'src/page-components/Downloads/Downloads'
import FAQ from 'src/page-components/FAQ/FAQ'
import InsightsPage from 'src/page-components/InsightsPage/InsightsPage'
import MarcomPricingPage from 'src/page-components/MarcomPricingPage/MarcomPricingPage'
import PricingPage from 'src/page-components/PricingPage/PricingPage'
import Privacy from 'src/page-components/Privacy/Privacy'
import StreamingAgentPage from 'src/page-components/StreamingAgentPage/StreamingAgentPage'
import Support from 'src/page-components/Support/Support'
import Terms from 'src/page-components/Terms/Terms'

export type FlatAppRoute = Omit<FlatAppRouteMetadata, 'path'> & {
  // A tabbed family registers ONE gate entry whose `path` is an array covering
  // every URL in that family. Matching any of them resolves the same entry, so
  // the page (and its persistent Subnav) stays mounted across tabs — see the
  // gate in App.tsx and src/component-layout/FamilyLayout. Single-page routes
  // keep using a plain string.
  path: string | string[]
  Component: ComponentType
}

export type AuthenticatedFlatAppRoute = Omit<
  AuthenticatedFlatAppRouteMetadata,
  'path'
> & {
  // See `FlatAppRoute.path` — tabbed families register one entry with an array
  // of paths.
  path: string | string[]
  Component: ComponentType
}

// Authed flat pages. These render in `Authed.tsx`'s one page <Switch>, as a
// block BEFORE the not-yet-migrated legacy routes — precedence is source order,
// no ranker. PARAM (`:id`) routes have an ordering hazard: a flat `:id` here is
// greedy and would shadow a same-depth static sibling still in the legacy
// registry. Before adding a param route, confirm its static siblings are flat
// too (or co-migrate them), and order specific-before-param. See
// `src/navigation/AGENTS.md` → "Flat route invariants".
export const AuthenticatedFlatArchitectureRoutes: AuthenticatedFlatAppRoute[] =
  [
    {
      ...PricingFlatRouteMetadata,
      Component: PricingPage,
    },
    {
      ...DownloadsFlatRouteMetadata,
      Component: Downloads,
    },
    {
      ...ActivityFlatRouteMetadata,
      Component: Activity,
    },
    {
      ...InsightsFlatRouteMetadata,
      Component: InsightsPage,
    },
    {
      ...StreamingAgentFlatRouteMetadata,
      Component: StreamingAgentPage,
    },
    {
      ...AccountingReviewReportsFlatRouteMetadata,
      Component: AccountingTransactionsShell,
    },
    {
      ...OnboardingFlatRouteMetadata,
      Component: Onboarding,
    },
    {
      ...OnboardingCardFlatRouteMetadata,
      Component: OnboardingCard,
    },
    {
      ...OrgInviteDecisionFlatRouteMetadata,
      Component: OrgInviteDecision,
    },
    {
      ...ManagerReviewTransactionsFlatRouteMetadata,
      Component: ExpensesNeedsApproval,
    },
    {
      ...ManagerReviewReimbursementsFlatRouteMetadata,
      Component: ManagerApproverExpenseReports,
    },
    {
      ...ExpenseReportFlatRouteMetadata,
      Component: ExpenseReportAccounting,
    },
    {
      ...ManagerReviewReimbursementDetailFlatRouteMetadata,
      Component: ExpenseReportManagerReview,
    },
    // EX-39748 — Budgets tabbed family. ONE gate entry per family; each `path`
    // array covers every URL in that family so the page + its FamilyLayout Subnav
    // stay mounted across tab navigation (preserving the sliding active-bar). The
    // page owns the nested tab routing. Paths derive from budgetsRouteTree.
    {
      path: budgetsFamilyGatePaths,
      Component: BudgetsFamilyPage,
      renderInAuthedShell: true,
    },
    {
      path: budgetsEntityGatePaths,
      Component: BudgetEntityPage,
      renderInAuthedShell: true,
    },
    // {
    //   path: Routes.CANCEL_SUBSCRIPTION_ROUTE(),
    //   Component: CancelSubscriptionPage,
    // },
    /* HYGEN-INJECT-ROUTE */
  ]

export const AuthenticationAgnosticFlatArchitectureRoutes: FlatAppRoute[] = [
  {
    ...FAQFlatRouteMetadata,
    Component: FAQ,
  },
  {
    ...TermsFlatRouteMetadata,
    Component: Terms,
  },
  {
    ...PrivacyFlatRouteMetadata,
    Component: Privacy,
  },
  {
    ...SupportFlatRouteMetadata,
    Component: Support,
  },
]

export const PublicFlatArchitectureRoutes: FlatAppRoute[] = [
  {
    ...MarcomPricingFlatRouteMetadata,
    Component: MarcomPricingPage,
  },
]
